import {
  TaskTrackerBoardCard,
  TaskTrackerBoardCardPerformer,
  TaskTrackerBoardCardUser,
} from '../store/taskTracker.types'

export const normalizeTaskPerformerIds = (ids: number[]) =>
  ids.filter((id) => Number.isFinite(id) && id > 0)

export const areTaskPerformerIdsEqual = (left: number[], right: number[]) => {
  if (left.length !== right.length) return false

  return left.every((id, index) => id === right[index])
}

export const hasTaskPerformers = (
  task: Pick<TaskTrackerBoardCard, 'performerIds' | 'performers'>,
) => {
  if (task.performerIds.length > 0) return true
  return task.performers.length > 0
}

export const formatTaskPerformerName = (
  performer: TaskTrackerBoardCardPerformer | TaskTrackerBoardCardUser,
  fallbackId?: number,
) => {
  const user =
    'user' in performer ? performer.user : performer
  const id =
    'userId' in performer
      ? performer.userId
      : user?.id ?? fallbackId

  const name = user?.name?.trim()
  if (name) return name
  if (id != null && Number.isFinite(id) && id > 0) return `ID ${id}`
  return '—'
}

export const formatTaskPerformersLabel = (
  performers: TaskTrackerBoardCardPerformer[],
  performerIds: number[] = [],
) => {
  const names = performers
    .map((performer) => formatTaskPerformerName(performer))
    .filter((name) => name !== '—')

  if (names.length > 0) {
    return names.join(', ')
  }

  const fallbackIds = normalizeTaskPerformerIds(performerIds)
  if (fallbackIds.length === 0) return '—'

  return fallbackIds.map((id) => `ID ${id}`).join(', ')
}

const PERFORMER_AVATAR_COLORS = [
  '#f97316',
  '#dc2626',
  '#8b5cf6',
  '#0ea5e9',
  '#16a34a',
  '#d97706',
] as const

export const getTaskPerformerAvatarLabel = (name: string) => {
  const trimmed = name.trim()
  if (!trimmed) return '?'

  const words = trimmed.split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    return `${words[0].slice(0, 1)}${words[1].slice(0, 1)}`.toUpperCase()
  }

  return trimmed.slice(0, 2).toUpperCase()
}

export const getTaskPerformerAvatarColor = (userId: number) =>
  PERFORMER_AVATAR_COLORS[
    Math.abs(userId) % PERFORMER_AVATAR_COLORS.length
  ]

export const getTaskCardVisiblePerformers = (
  performers: TaskTrackerBoardCardPerformer[],
  limit = 3,
) => performers.slice(0, limit)
